import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Button,
  Spinner,
  Badge,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import { formatNumber } from '../../utils/transform';
import { getInvoiceTypeLabel } from '../../utils/invoiceTypes';

const columnHelper = createColumnHelper();

export default function InvoicesTable({ 
  columnsData, 
  data, 
  handlePrintInvoice, 
  page = 0, 
  setPage, 
  totalPages = 0,
  isLoading = false,
  total = 0,
  sorting,
  onSortingChange,
}) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = React.useMemo(() => createColumns(columnsData), [columnsData]);

  const table = useSortableAdminTable({
    data: data || [],
    columns,
    sorting,
    onSortingChange,
  });

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => {
          let value = info.getValue();
          const rowData = info.row.original;

          // Manejar diferentes estructuras de datos (proveedor vs base local)
          if (col.accessor === 'tipo_comprobante') {
            value = value ?? rowData.tipo_comprobante ?? rowData.invoiceType;
            value = getInvoiceTypeLabel(value) || value;
          }

          if (col.accessor === 'serie') {
            value = value || rowData.series;
          }

          if (col.accessor === 'numero') {
            value = value || rowData.number;
          }

          if (col.accessor === 'fecha_emison') {
            const dateValue = value || rowData.fecha_emison || rowData.issueDate;
            if (dateValue) {
              const date = new Date(dateValue);
              if (!Number.isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                value = `${day}/${month}/${year}`;
              } else {
                value = null;
              }
            }
          }

          if (col.accessor === 'receptor.denominacion') {
            // Manejar tanto receptor.denominacion como customer.name
            value = value || rowData.receptor?.denominacion || rowData.customer?.name || 'N/A';
          }

          if (col.accessor === 'totales.total') {
            // Manejar tanto totales.total como invoiceTotals.total
            value = value || rowData.totales?.total || rowData.invoiceTotals?.total || 0;
            value = formatNumber(value);
          }

          if (col.accessor === 'estado_dgi.codigo') {
            // Manejar estado DGI
            const estado = value || rowData.estado_dgi?.codigo || rowData.invoiceDgiStatus?.code;
            if (estado) {
              return (
                <Badge colorScheme={String(estado) === '1' ? 'green' : 'orange'} size="sm">
                  {String(estado) === '1' ? 'Aprobado' : `Estado ${estado}`}
                </Badge>
              );
            }
            return <Badge colorScheme="gray" size="sm">Sin estado</Badge>;
          }

          // Formatear números para el número de factura
          if (col.accessor === 'numero' && value) {
            value = formatNumber(value);
          }

          if (col.actions?.length) {
            return (
              <Flex align="center" gap={1}>
                {col.actions.map((action, index) => (
                  <IconButton
                    key={index}
                    aria-label={action.label}
                    icon={action.icon}
                    size="sm"
                    variant="ghost"
                    colorScheme={action.colorScheme}
                    onClick={(event) => {
                      event.stopPropagation();
                      handlePrintInvoice(action, info.row.original);
                    }}
                  />
                ))}
              </Flex>
            );
          }

          return (
            <Flex align="center">
              <Text fontSize="sm" fontWeight="normal">
                {value || 'N/A'}
              </Text>
            </Flex>
          );
        },
      }),
    );
  }

  if (isLoading) {
    return (
      <PageTable flexDirection="column" w="100%" px="0px" py="40px">
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" color="blue.500" />
          <Text ml={4} color="gray.600">Cargando facturas...</Text>
        </Flex>
      </PageTable>
    );
  }

  return (
    <PageTable flexDirection="column" w="100%" px="0px">
      <Box>
        <Flex justify="space-between" align="center" px="25px" py="10px">
          <Text fontSize="sm" color="gray.600">
            {total > 0
              ? `Mostrando ${data.length} de ${total} facturas`
              : 'No se encontraron facturas'}
          </Text>
        </Flex>

        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader
                      key={header.id}
                      header={header}
                      borderColor={borderColor}
                    />
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {data.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={columnsData.length} textAlign="center" py="40px">
                    <Text color="gray.500">
                      No se encontraron facturas con los criterios especificados
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </PageTable.Scroll>

        {totalPages > 1 && (
          <Flex
            justifyContent="space-between"
            align="center"
            mt="4"
            px="25px"
            pb="20px"
          >
            <Button
              onClick={() => setPage(Math.max(page - 1, 0))}
              isDisabled={page === 0}
              size="sm"
            >
              Anterior
            </Button>
            <Text fontSize="sm" color="gray.600">
              Página {page + 1} de {totalPages}
            </Text>
            <Button
              onClick={() => setPage(Math.min(page + 1, totalPages - 1))}
              isDisabled={page + 1 >= totalPages}
              size="sm"
            >
              Siguiente
            </Button>
          </Flex>
        )}
      </Box>
    </PageTable>
  );
}
